import React, {useEffect, useMemo, useState} from "react";
import {useFormik} from "formik";
import * as yup from "yup";
import {getValidation, SetupConfig} from "./config";
import QButton from "../../../common/components/QButton";
import {config} from "../../../config";
import {useDispatch, useSelector} from "react-redux";
import {setLoading} from "../../../app/rootReducer";
import {ResponseInterface} from "../../../interfaces/ResponseInterface";
import SetupApi from "../../../common/api/functions/Setup";
import {SetupInterface} from "../../../interfaces/private/SetupInterface";
import QLoading from "../../../common/components/QLoading";
import {RootState} from "../../../app/store";
import {Progress} from "reactstrap";
import {QHelpers} from "../../../common/helpers";
import {useTranslation} from "react-i18next";
import {
    buildWizardSteps,
    normalizeEnabledModules,
    SyncModuleKey,
    WizardStepKey,
} from "./helpers/onboardingModules";
import {refreshAccountSetup} from "../../../common/helpers/refreshAccountSetup";
import {resolveOnboardingPath} from "../../../common/helpers/onboardingResume";
import {useHistory} from "react-router-dom";
import {RouteDefine} from "../../../routes/RouteDefine";
import {findFirstIncompleteWizardStepIndex} from "../../../common/helpers/setupCompleteness";
import WizardStepper from "./components/WizardStepper";
import {applyInvoiceSetupDefaults} from "./helpers/invoiceDefaults";
import {applyCommonSetupDefaults, COMMON_DEFAULT_KEYS} from "./helpers/commonDefaults";
import {needsCommonSetup} from "./helpers/onboardingModules";

interface Props {

}

const defaultSetupValues: SetupInterface = {
    stripe_invoice_source: 'invoice',
    stripe_checkout_prices_include_vat: false,
    stripe_checkout_included_vat_rate: 25,
    sync_engine_version: 'v2',
    enabled_sync_modules: [] as SyncModuleKey[],
    economic_invoice_booked_with: '1',
    economic_invoice_send_out: false,
    economic_invoice_vat_zone_domestic_number: '',
    economic_invoice_vat_zone_eu_number: '',
    economic_invoice_vat_zone_abroad_number: '',
    economic_invoice_vat_zone_domestic_without_vat_number: '',
    economic_payment_matching_invoice: false,
};

/** API JSON nulls overwrite defaults — coerce to '' so selects/Yup don't see null. */
const sanitizeSetupNulls = (data: Record<string, any>): Record<string, any> => {
    const next: Record<string, any> = {};
    Object.keys(data).forEach((key) => {
        next[key] = data[key] === null ? '' : data[key];
    });
    return next;
};

const Setup: React.FC<Props> = ({}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const {t} = useTranslation();
    const [stepIndex, setStepIndex] = useState(0);
    const [terminalStep, setTerminalStep] = useState<'success' | 'error' | null>(null);
    const {loading} = useSelector((state: RootState) => state.rootReducer);
    const [direction, setDirection] = useState(1);
    const [bootstrapping, setBootstrapping] = useState(true);
    const [initialValues, setInitialValues] = useState<SetupInterface>({...defaultSetupValues});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const account = await refreshAccountSetup();
            if (cancelled) return;
            if (account) {
                const path = resolveOnboardingPath(account);
                if (path === RouteDefine.confirm) {
                    dispatch(setLoading(false));
                    history.replace(RouteDefine.confirm);
                    return;
                }
            }

            try {
                const response: ResponseInterface | any = await SetupApi.detail();
                const setupData: SetupInterface = response?.data?.data || {};
                if (cancelled) return;

                const sanitized = sanitizeSetupNulls(setupData as Record<string, any>);
                const modules = normalizeEnabledModules(sanitized.enabled_sync_modules);
                const merged: SetupInterface = {
                    ...defaultSetupValues,
                    ...sanitized,
                    enabled_sync_modules: modules,
                    stripe_invoice_source: sanitized.stripe_invoice_source === 'checkout' ? 'checkout' : 'invoice',
                    stripe_checkout_prices_include_vat: !!sanitized.stripe_checkout_prices_include_vat,
                    stripe_checkout_included_vat_rate: (() => {
                        const rate = sanitized.stripe_checkout_included_vat_rate;
                        if (rate === '' || rate === undefined) {
                            return 25;
                        }
                        const n = Number(rate);
                        return Number.isFinite(n) ? n : 25;
                    })(),
                    economic_payment_matching_invoice: !!sanitized.economic_payment_matching_invoice,
                    sync_engine_version: 'v2',
                };
                setInitialValues(merged);

                const steps = buildWizardSteps(modules);
                setStepIndex(findFirstIncompleteWizardStepIndex(merged, steps.map((s) => s.key)));
            } catch {
                // Keep defaults if hydrate fails
            }

            if (!cancelled) {
                dispatch(setLoading(false));
                setBootstrapping(false);
            }
        })();
        return () => { cancelled = true; };
    }, [history, dispatch]);


    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: yup.object().shape({}),
        onSubmit: async () => {
            // handled by handleNext
        },
    });

    const modules = normalizeEnabledModules(formik.values.enabled_sync_modules);
    const wizardSteps = useMemo(() => buildWizardSteps(modules), [modules.join(',')]);

    useEffect(() => {
        if (stepIndex > wizardSteps.length - 1) {
            setStepIndex(Math.max(0, wizardSteps.length - 1));
        }
    }, [wizardSteps.length, stepIndex]);

    const currentStep = terminalStep
        ? {key: terminalStep as WizardStepKey, labelKey: 'common.setup.stepDone'}
        : wizardSteps[Math.min(stepIndex, wizardSteps.length - 1)];

    const totalConfigSteps = wizardSteps.length;
    const progressValue = terminalStep === 'success'
        ? 100
        : Math.max(5, (100 / Math.max(totalConfigSteps, 1)) * (stepIndex + 1));

    const valuesForPersist = (values: SetupInterface): SetupInterface => {
        let withModules = {
            ...values,
            enabled_sync_modules: normalizeEnabledModules(values.enabled_sync_modules),
            sync_engine_version: 'v2' as const,
        };
        if (currentStep.key === 'invoice') {
            withModules = applyInvoiceSetupDefaults(withModules) as typeof withModules;
        }
        if (
            currentStep.key === 'invoice'
            || currentStep.key === 'payment'
            || currentStep.key === 'subscription'
            || needsCommonSetup(normalizeEnabledModules(withModules.enabled_sync_modules))
        ) {
            withModules = applyCommonSetupDefaults(withModules) as typeof withModules;
        }
        return withModules as SetupInterface;
    };

    const persistPartial = async (values: SetupInterface) => {
        const payload = valuesForPersist(values);
        if (currentStep.key === 'invoice') {
            const keys: (keyof SetupInterface)[] = [
                'economic_invoice_booked_with',
                'economic_invoice_send_out',
                'economic_invoice_other_ref',
                'economic_invoice_vat_zone_domestic_number',
                'economic_invoice_vat_zone_eu_number',
                'economic_invoice_vat_zone_abroad_number',
                'economic_invoice_vat_zone_domestic_without_vat_number',
                'economic_invoice_layout_number',
                'economic_invoice_payment_terms_number',
                ...COMMON_DEFAULT_KEYS,
            ];
            keys.forEach((key) => {
                if (payload[key] !== values[key]) {
                    formik.setFieldValue(key as string, payload[key]);
                }
            });
        } else if (currentStep.key === 'payment' || currentStep.key === 'subscription') {
            COMMON_DEFAULT_KEYS.forEach((key) => {
                if (payload[key] !== values[key]) {
                    formik.setFieldValue(key as string, payload[key]);
                }
            });
        }
        await SetupApi.update(payload);
    };

    const markLoggedIn = async () => {
        let accountData = JSON.parse(QHelpers.store.localStorageGetItem(config.LOCAL_STORE.ACCOUNT) ?? '{}');
        accountData = {
            ...accountData,
            user: {
                ...accountData.user,
                is_logged: 1,
            },
            step: -1,
        };
        await localStorage.setItem(config.LOCAL_STORE.ACCOUNT, JSON.stringify(accountData));
    };

    const finishSetup = async (values: SetupInterface) => {
        dispatch(setLoading(true));
        try {
            const response: ResponseInterface | any = await SetupApi.update(valuesForPersist(values));
            dispatch(setLoading(false));
            if (!response?.data?.error) {
                await markLoggedIn();
                setTerminalStep('success');
            } else {
                setTerminalStep('error');
            }
        } catch {
            dispatch(setLoading(false));
            setTerminalStep('error');
        }
    };

    const validateCurrentStep = async (): Promise<boolean> => {
        const schema = getValidation(currentStep.key, yup);
        try {
            await schema.validate(formik.values, {abortEarly: false});
            formik.setErrors({});
            return true;
        } catch (err: any) {
            const errors: Record<string, string> = {};
            if (err?.inner?.length) {
                err.inner.forEach((item: any) => {
                    if (item.path && !errors[item.path]) {
                        errors[item.path] = item.message;
                    }
                });
            } else if (err?.path && err?.message) {
                errors[err.path] = err.message;
            }
            formik.setErrors(errors);
            formik.setTouched(
                Object.keys(errors).reduce((acc, key) => ({...acc, [key]: true}), {}),
                false
            );
            return false;
        }
    };

    const handleNext = async () => {
        const ok = await validateCurrentStep();
        if (!ok) {
            return;
        }

        dispatch(setLoading(true));
        setDirection(1);

        try {
            await persistPartial(formik.values as SetupInterface);
        } catch {
            dispatch(setLoading(false));
            setTerminalStep('error');
            return;
        }

        const isLastConfigStep = stepIndex >= wizardSteps.length - 1;
        if (isLastConfigStep) {
            await finishSetup(formik.values as SetupInterface);
            return;
        }

        setStepIndex(stepIndex + 1);
        dispatch(setLoading(false));
    };

    const handleSkip = async (event: React.MouseEvent) => {
        event.preventDefault();
        if (currentStep.key === 'modules' || terminalStep) {
            return;
        }

        if (!window.confirm(t("common.setup.skipConfirm"))) {
            return;
        }

        dispatch(setLoading(true));
        setDirection(1);

        try {
            await persistPartial(formik.values as SetupInterface);
        } catch {
            dispatch(setLoading(false));
            setTerminalStep('error');
            return;
        }

        const isLastConfigStep = stepIndex >= wizardSteps.length - 1;
        if (isLastConfigStep) {
            await finishSetup(formik.values as SetupInterface);
            return;
        }

        setStepIndex(stepIndex + 1);
        dispatch(setLoading(false));
    };

    const handleBack = () => {
        if (stepIndex <= 0 || terminalStep) {
            return;
        }
        setDirection(-1);
        dispatch(setLoading(true));
        setStepIndex(stepIndex - 1);
        dispatch(setLoading(false));
    };

    const canSkip = !terminalStep && currentStep.key !== 'modules';
    const showNav = !terminalStep;

    const progressLabel = terminalStep
        ? t(terminalStep === 'success' ? 'common.setup.stepDone' : 'common.setup.stepError')
        : t("common.setup.stepProgress", {
            current: stepIndex + 1,
            total: totalConfigSteps,
            label: t(currentStep.labelKey),
        });

    if (bootstrapping) {
        return <QLoading loading={true}/>;
    }

    return (
        <React.Fragment>
            <QLoading loading={loading}/>
            <div className="mt-5 position-relative">
                <WizardStepper
                    steps={wizardSteps}
                    currentIndex={stepIndex}
                    terminal={terminalStep}
                />
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">{progressLabel}</small>
                </div>
                <Progress style={{height: '15px'}} color="danger" value={progressValue}/>
                <form method="">
                    {SetupConfig(
                        currentStep.key,
                        direction,
                        formik,
                        terminalStep ? undefined : currentStep.labelKey,
                    )}
                    {canSkip ? (
                        <div className="mt-2 text-end">
                            <a className={'cl-orange-zp'} href={'#'} onClick={handleSkip}>
                                {t("common.setup.skipForNow")}
                            </a>
                            <div className="text-muted small mt-1">{t("common.setup.skipHelp")}</div>
                        </div>
                    ) : null}
                    {showNav ? (
                        <div className="mt-5 d-flex gap-2">
                            {stepIndex > 0 ? (
                                <QButton
                                    className={'btn bdr-10 fs-16 mt-3'}
                                    width={100}
                                    content={t("common.setup.back")}
                                    size={''}
                                    onClick={handleBack}
                                />
                            ) : null}
                            <QButton
                                className={'btn bdr-10 fs-16 mt-3'}
                                width={100}
                                type={'primary'}
                                content={
                                    stepIndex >= wizardSteps.length - 1
                                        ? t("common.button.submit")
                                        : t("common.setup.next")
                                }
                                size={''}
                                onClick={handleNext}
                            />
                        </div>
                    ) : null}
                </form>
            </div>
        </React.Fragment>
    );
};

export default Setup;
